import { Injectable, inject } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { map, Observable } from "rxjs";
import { environment } from "@environments/environment";
import {
  EligibilitySelection,
  TinEligibilityDetails,
  MipsSettings,
  SaveStepResponse,
  SubmitPacketResponse,
  PiMeasurePdfResource,
  ImprovementActivity,
  DrcfDocument,
  DrcfStatus,
} from "@models/mips-stepper.model";
import { MipsFileRepositoryItem } from "@models/mips-file-repository.model";

// ============================================
// RESPONSE INTERFACES
// ============================================

export interface GetSubmissionResponse {
  submissionId: number;
  organizationId: number;
  programName: string;
  entityType: string;
  tin: string;
  npi: string;
  mvpid?: string;
  cehrtId?: string;
  performanceYear: number;
  performanceStart: string;
  performanceEnd: string;
  submissionStatus: string;
  submittedDate?: string;
  externalSubmissionId?: string;
  feedbackEmail?: string;
  tinStatus?: string;
  tinStatusUpdatedByUserID?: number;
  tinStatusUpdatedDate?: string;
  providerIds?: number[];
  documentName?: string;
  documentUrl?: string;
  isFifteenOrFewerClinicians?: boolean;
  hasExtremeCircumstancesException?: boolean;
  hasExtremeCircumstancesExceptionQuality?: boolean;
  hasExtremeCircumstancesExceptionPI?: boolean;
  hasExtremeCircumstancesExceptionIA?: boolean;
  hasExtremeCircumstancesExceptionCost?: boolean;
  allCliniciansEligibleForPIReweighting?: boolean;
  hasPIHardshipExceptionApproved?: boolean;
  isNonPatientFacingGroup?: boolean;
  isDesignatedRuralArea?: boolean;
  isHPSADesignated?: boolean;
  piAuditAttestation?: boolean;
  oncDirectReview?: boolean;
  interoperabilityAttestation?: boolean;
  securityRiskAnalysis?: boolean;
  securityRiskAnalysisImplemented?: boolean;
  saferGuidesAttestation?: boolean;
  measurementSets: any[];
  promotingInteroperability?: {
    estimatedPoints: number;
    estimatedBonus: number;
    estimatedTotal: number;
    estimatedWeight: number;
    maxWeight: number;
    allRequiredComplete: boolean;
    attestationsComplete: boolean;
    incompleteMeasureIds: string[];
    measureScores: PiMeasureScore[];
  } | null;
  improvementActivities?: {
    estimatedPoints: number;
    estimatedWeight: number;
    maxWeight: number;
    measureScores: IaMeasureScore[];
  } | null;
  qualityScores?: QualityScoresBlock | null;
  piDocuments: Array<{
    documentId: number;
    fileName: string;
    documentUrl: string;
  }>;
  iaDocuments: Array<{
    documentId: number;
    fileName: string;
    name: string;
    documentUrl: string;
  }>;
}

export interface RefreshMvpRegistrationResponse {
  submissionId: number;
  mvpRegistered: boolean;
  mvpid?: string;
}

// Quality Interfaces
export interface QualityMeasurementDto {
  measureId: string;
  numerator: number;
  numeratorExclusion?: number | null;
  denominator: number;
  ipp: number;
  denominatorException?: number | null;
  denominatorExclusion?: number | null;
  isEndToEndReported: boolean;
  stratum?: string | null;
}

/**
 * A selected quality measure's already-computed achievement points, echoed back from the
 * dashboard measure list the frontend just loaded — the save handler trusts this rather than
 * recomputing it, since selection doesn't change any measure's own points, only which ones
 * count toward the top-N category sum.
 */
export interface QualityMeasureScoreInputDto {
  measureCode: string;
  calculatedPoints: number | null;
  isMIPS: boolean;
  isMVP: boolean;
}

export interface GetSubmissionScoreResponse {
  data: {
    mips: {
      value: number | null;
    } | null;
  } | null;
}

export interface SubmissionCheckCms {
  processingStatus: string | null;
  isInverse: boolean | null;
  messages: Record<string, string>;
}

export interface SubmissionCheckRow {
  measureId: string;
  measureCode: string | null;
  stratum: string | null;
  measureType: string | null;
  isInverted: boolean;
  dashboardNumerator: number | null;
  dashboardDenominator: number | null;
  dashboardRate: number | null;
  sentNumerator: number | null;
  sentDenominator: number | null;
  sentRate: number | null;
  cmsNumerator: number | null;
  cmsDenominator: number | null;
  cmsRate: number | null;
  isMatch: boolean;
  issues: string[];
  cms: SubmissionCheckCms | null;
  sentJson: string | null;
  cmsJson: string | null;
}

export interface SubmissionCheckResult {
  submissionId: number;
  qppSubmissionId: string | null;
  sentDate: string | null;
  dashboardError: string | null;
  sentError: string | null;
  cmsError: string | null;
  mismatchCount: number;
  rows: SubmissionCheckRow[];
}

export interface QualityMeasureAchievementScore {
  measureCode: string;
  achievementPoints: number;
}

export interface QualityScoresBlock {
  measureScores: QualityMeasureAchievementScore[];
  traditionalPoints: number;
  traditionalTotal: number;
  traditionalWeightedScore: number;
  mvpPoints: number;
  mvpTotal: number;
  mvpWeightedScore: number;
}

export interface SaveQualityMeasurementsResponse {
  measurementSetId: number;
  measurementCount: number;
  status: string;
  qualityScores?: QualityScoresBlock | null;
}

// PI Interfaces
export interface PIMeasurementDto {
  measureId: string;
  valueType: string;
  booleanValue?: boolean | null;
  numerator?: number | null;
  denominator?: number | null;
  activeEngagementLevel?: string | null;
  exclusionId?: string | null;
}

export interface SavePIAttestationsRequest {
  piAuditAttestation: boolean;
  oncDirectReview: "yes" | "no" | null;
  interoperabilityAttestation: "yes" | "no" | null;
  securityRiskAnalysis: "yes" | "no" | null;
  securityRiskAnalysisImplemented: "yes" | "no" | null;
  saferGuidesAttestation: "yes" | "no" | null;
}

export interface SavePIAttestationsResponse {
  questionnaireId: number;
  piAuditAttestation: "yes" | "no";
  oncDirectReview: "yes" | "no" | null;
  interoperabilityAttestation: "yes" | "no" | null;
  securityRiskAnalysis: "yes" | "no" | null;
  securityRiskAnalysisImplemented: "yes" | "no" | null;
  saferGuidesAttestation: "yes" | "no" | null;
}

export function parseAttestationBool(val: any): boolean | null {
  if (val === true || val === "yes") return true;
  if (val === false || val === "no") return false;
  return null;
}

export interface SavePIMeasurementsRequest {
  performanceStart: Date;
  performanceEnd: Date;
  measurements: PIMeasurementDto[];
}

export interface PiMeasureScore {
  measureId: string;
  computedPoints: number;
  effectiveMaxPoints: number;
  redistributedPoints: number;
  bonusPoints: number;
  showBonusPoints: boolean;
}

export interface SavePIMeasurementsResponse {
  measurementSetId: number;
  measurementCount: number;
  exclusionCount: number;
  status: string;
  piEstimatedPoints: number;
  piEstimatedBonus: number;
  piEstimatedTotal: number;
  piEstimatedWeight: number;
  piMaxWeight: number;
  allRequiredComplete: boolean;
  attestationsComplete: boolean;
  incompleteMeasureIds: string[];
  measureScores: PiMeasureScore[];
}

export interface UploadPIDocumentResponse {
  documentId: number;
  fileName: string;
  documentUrl: string;
}

// IA Interfaces
export interface IaMeasureScore {
  measureId: string;
  points: number;
  isHighWeighted: boolean;
}

export interface SaveIAMeasurementsResponse {
  measurementSetId: number;
  measurementCount: number;
  status: string;
  iaEstimatedPoints: number;
  iaEstimatedWeight: number;
  iaMaxWeight: number;
  measureScores: IaMeasureScore[];
}

export interface UploadIADocumentResponse {
  documentId: number;
  fileName: string;
  measureId: string;
  documentUrl: string;
}

// ============================================
// SERVICE
// ============================================

@Injectable({
  providedIn: "root",
})
export class MipsApiService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/mips/submissions`;
  private readonly fileRepositoryUrl = `${environment.apiUrl}/mips/file-repository`;

  // ============================================
  // SUBMISSION MANAGEMENT
  // ============================================

  createSubmission(
    eligibility: EligibilitySelection,
    tin?: string,
    providerIds?: number[],
    mvpId?: string | null
  ): Observable<{ submissionId: number }> {
    const isMvp = eligibility.reportingType === "mvp";
    const payload: Record<string, unknown> = {
      organizationId: parseInt(eligibility.practice, 10),
      programName: isMvp ? "mvp" : "mips",
      entityType: eligibility.reportingLevel,
      tin: tin || eligibility.tin || null,
      npi: eligibility.npi,
      performanceYear: eligibility.performanceYear,
      performanceStart: new Date(
        eligibility.performanceYear,
        0,
        1
      ).toISOString(),
      performanceEnd: new Date(
        eligibility.performanceYear,
        11,
        31
      ).toISOString(),
      reportingMethod: eligibility.reportingMethod,
      providerIds: providerIds || null,
    };

    if (isMvp && mvpId) {
      payload['mvpId'] = mvpId;
    }

    return this.http.post<{ submissionId: number }>(`${this.apiUrl}`, payload);
  }

  getSubmission(submissionId: number): Observable<GetSubmissionResponse> {
    return this.http.get<GetSubmissionResponse>(
      `${this.apiUrl}/${submissionId}`
    );
  }

  submitPacket(submissionId: number): Observable<SubmitPacketResponse> {
    return this.http.post<SubmitPacketResponse>(
      `${this.apiUrl}/${submissionId}/finalize`,
      {}
    );
  }

  refreshMvpRegistration(
    submissionId: number
  ): Observable<RefreshMvpRegistrationResponse> {
    return this.http.put<RefreshMvpRegistrationResponse>(
      `${this.apiUrl}/${submissionId}/mvp`,
      {}
    );
  }

  getSubmissionScore(submissionId: number): Observable<GetSubmissionScoreResponse> {
    return this.http.get<GetSubmissionScoreResponse>(`${this.apiUrl}/${submissionId}/score`);
  }

  getSubmissionCheck(
    submissionId: number,
    shouldIncludeRawJson: boolean = false
  ): Observable<SubmissionCheckResult> {
    const params = shouldIncludeRawJson
      ? new HttpParams().set("includeRawJson", "true")
      : undefined;

    return this.http.get<SubmissionCheckResult>(
      `${this.apiUrl}/${submissionId}/submission-check`,
      { params }
    );
  }

  // ============================================
  // TIN DETAILS
  // ============================================

  saveTinDetails(
    submissionId: number,
    tinDetails: TinEligibilityDetails
  ): Observable<SaveStepResponse> {
    const payload = {
      TIN: tinDetails.tin,
      PerformanceStart: tinDetails.performanceStartDate,
      PerformanceEnd: tinDetails.performanceEndDate,
      FeedbackEmail: tinDetails.emailForFeedback,
      ProviderIds: tinDetails.selectedProviders,
    };

    return this.http.put<SaveStepResponse>(
      `${this.apiUrl}/${submissionId}/tin`,
      payload
    );
  }

  requestTinApproval(submissionId: number): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      `${this.apiUrl}/${submissionId}/request-tin-approval`,
      {}
    );
  }

  approveTin(submissionId: number): Observable<{
    submissionId: number;
    tinStatus: string;
    approvedDate: string;
  }> {
    return this.http.post<{
      submissionId: number;
      tinStatus: string;
      approvedDate: string;
    }>(`${this.apiUrl}/${submissionId}/tin/approve`, {});
  }

  rejectTin(
    submissionId: number
  ): Observable<{ submissionId: number; tinStatus: string }> {
    return this.http.post<{ submissionId: number; tinStatus: string }>(
      `${this.apiUrl}/${submissionId}/tin/reject`,
      {}
    );
  }

  uploadTinDocument(
    submissionId: number,
    file: File
  ): Observable<{ documentId: number; fileName: string; documentUrl: string }> {
    const formData = new FormData();
    formData.append("file", file);

    return this.http.post<{
      documentId: number;
      fileName: string;
      documentUrl: string;
    }>(`${this.apiUrl}/${submissionId}/tin/document`, formData);
  }

  deleteTinDocument(submissionId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/${submissionId}/tin/document`
    );
  }

  downloadTinDocument(
    submissionId: number,
    documentType: string = "cms1500"
  ): Observable<Blob> {
    const params = new HttpParams().set("documentType", documentType);
    return this.http.get(
      `${this.apiUrl}/${submissionId}/tin/document/download`,
      {
        params,
        responseType: "blob",
      }
    );
  }

  // ============================================
  // SETTINGS
  // ============================================

  validateCehrtId(cehrtId: string): Observable<boolean> {
    const params = new HttpParams().set("cehrtId", cehrtId);
    return this.http.get<boolean>(`${this.apiUrl}/cehrt/validate`, { params });
  }

  saveSettings(
    submissionId: number,
    settings: MipsSettings,
    organizationId: number,
    performanceYear: number
  ): Observable<SaveStepResponse> {
    const payload = {
      CehrtId: settings.cehrtId,
    };

    const params = new HttpParams()
      .set("organizationId", organizationId.toString())
      .set("performanceYear", performanceYear.toString());

    return this.http.put<SaveStepResponse>(
      `${this.apiUrl}/${submissionId}/settings`,
      payload,
      { params }
    );
  }

  // ============================================
  // QUALITY MEASURES
  // ============================================

  saveQualityMeasurements(
    submissionId: number,
    measurements: QualityMeasurementDto[],
    measureScores: QualityMeasureScoreInputDto[] = []
  ): Observable<SaveQualityMeasurementsResponse> {
    const payload = { measurements, measureScores };
    return this.http.post<SaveQualityMeasurementsResponse>(
      `${this.apiUrl}/${submissionId}/quality`,
      payload
    );
  }

  // ============================================
  // PROMOTING INTEROPERABILITY (PI)
  // ============================================

  savePIAttestations(
    submissionId: number,
    attestations: SavePIAttestationsRequest,
    organizationId: number,
    performanceYear: number
  ): Observable<SavePIAttestationsResponse> {
    const params = new HttpParams()
      .set("organizationId", organizationId.toString())
      .set("performanceYear", performanceYear.toString());

    const payload: any = {
      PIAuditAttestation: attestations.piAuditAttestation === true ? "yes" : "no",
    };

    if (attestations.oncDirectReview != null) {
      payload.ONCDirectReview = attestations.oncDirectReview;
    }
    if (attestations.interoperabilityAttestation != null) {
      payload.InteroperabilityAttestation = attestations.interoperabilityAttestation;
    }
    if (attestations.securityRiskAnalysis != null) {
      payload.SecurityRiskAnalysis = attestations.securityRiskAnalysis;
    }
    if (attestations.securityRiskAnalysisImplemented != null) {
      payload.SecurityRiskAnalysisImplemented = attestations.securityRiskAnalysisImplemented;
    }
    if (attestations.saferGuidesAttestation != null) {
      payload.SAFERGuidesAttestation = attestations.saferGuidesAttestation;
    }

    return this.http.post<SavePIAttestationsResponse>(
      `${this.apiUrl}/${submissionId}/pi/attestations`,
      payload,
      { params }
    );
  }

  savePIMeasurements(
    submissionId: number,
    request: SavePIMeasurementsRequest
  ): Observable<SavePIMeasurementsResponse> {
    const payload = {
      PerformanceStart: request.performanceStart,
      PerformanceEnd: request.performanceEnd,
      Measurements: request.measurements.map((m) => ({
        MeasureId: m.measureId,
        ValueType: m.valueType,
        BooleanValue: m.booleanValue,
        Numerator: m.valueType === "Proportion" ? (m.numerator ?? 0) : m.numerator,
        Denominator: m.valueType === "Proportion" ? (m.denominator ?? 0) : m.denominator,
        ActiveEngagementLevel: m.activeEngagementLevel,
        ExclusionId: m.exclusionId,
      })),
    };

    return this.http.post<SavePIMeasurementsResponse>(
      `${this.apiUrl}/${submissionId}/pi/measurements`,
      payload
    );
  }

  uploadPIDocument(
    submissionId: number,
    file: File
  ): Observable<UploadPIDocumentResponse> {
    const formData = new FormData();
    formData.append("file", file);

    return this.http.post<UploadPIDocumentResponse>(
      `${this.apiUrl}/${submissionId}/pi/document`,
      formData
    );
  }

  deletePIDocument(submissionId: number, documentId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/${submissionId}/pi/document/${documentId}`
    );
  }

  downloadPIDocument(
    submissionId: number,
    documentId: number,
    asAttachment: boolean = true
  ): Observable<Blob> {
    const params = new HttpParams().set(
      "asAttachment",
      asAttachment.toString()
    );

    return this.http.get(
      `${this.apiUrl}/${submissionId}/pi/document/${documentId}/download`,
      {
        params,
        responseType: "blob",
      }
    );
  }

  viewPIDocument(submissionId: number, documentId: number): Observable<Blob> {
    return this.downloadPIDocument(submissionId, documentId, false);
  }

  getPiMeasurePdfResources(year: number): Observable<PiMeasurePdfResource[]> {
    const params = new HttpParams().set("year", year.toString());
    return this.http.get<PiMeasurePdfResource[]>(
      `${environment.apiUrl}/mips/pi-measures`,
      { params },
    );
  }

  viewPiMeasurePdf(resourceUrl: string): Observable<Blob> {
    // Handles a fully-qualified same-origin URL without double-prefixing it;
    // anything else falls through to string concatenation so a request to an
    // unexpected host fails loudly instead of silently carrying our bearer token there.
    const requestUrl = resourceUrl.startsWith(environment.apiUrl)
      ? resourceUrl
      : `${environment.apiUrl}${resourceUrl}`;

    return this.http.get(requestUrl, {
      responseType: "blob",
    });
  }

  // ============================================
  // IMPROVEMENT ACTIVITIES (IA)
  // ============================================

  saveIAMeasurements(
    submissionId: number,
    performanceStart: Date,
    performanceEnd: Date,
    measurements: Array<{ measureId: string; booleanValue: boolean }>
  ): Observable<SaveIAMeasurementsResponse> {
    const payload = {
      PerformanceStart: performanceStart,
      PerformanceEnd: performanceEnd,
      Measurements: measurements.map((m) => ({
        MeasureId: m.measureId,
        ValueType: "Boolean",
        BooleanValue: m.booleanValue,
      })),
    };

    return this.http.post<SaveIAMeasurementsResponse>(
      `${this.apiUrl}/${submissionId}/ia/measurements`,
      payload
    );
  }

  uploadIADocument(
    submissionId: number,
    measureId: string,
    file: File
  ): Observable<UploadIADocumentResponse> {
    const formData = new FormData();
    formData.append("file", file);

    const params = new HttpParams().set("measureId", measureId);

    return this.http.post<UploadIADocumentResponse>(
      `${this.apiUrl}/${submissionId}/ia/document`,
      formData,
      { params }
    );
  }

  deleteIADocument(submissionId: number, measureId: string): Observable<void> {
    const params = new HttpParams().set("measureId", measureId);

    return this.http.delete<void>(
      `${this.apiUrl}/${submissionId}/ia/document`,
      {
        params,
      }
    );
  }

  downloadIADocument(
    submissionId: number,
    measureId: string,
    asAttachment: boolean = true
  ): Observable<Blob> {
    const params = new HttpParams()
      .set("measureId", measureId)
      .set("asAttachment", asAttachment.toString());

    return this.http.get(
      `${this.apiUrl}/${submissionId}/ia/document/download`,
      {
        params,
        responseType: "blob",
      }
    );
  }

  viewIADocument(submissionId: number, measureId: string): Observable<Blob> {
    return this.downloadIADocument(submissionId, measureId, false);
  }

  getImprovementActivities(): Observable<ImprovementActivity[]> {
    return this.http.get<ImprovementActivity[]>(
      `${environment.apiUrl}/mips/improvement-activities`
    );
  }

  // ============================================
  // DRCF
  // ============================================

  createDrcfEnvelope(
    submissionId: number,
    recipientEmail: string
  ): Observable<{ envelopeId: string }> {
    const payload = {
      RecipientEmail: recipientEmail,
    };
    return this.http.post<{ envelopeId: string }>(
      `${this.apiUrl}/${submissionId}/drcf/envelope`,
      payload
    );
  }

  getDrcfEnvelopes(submissionId: number): Observable<DrcfDocument[]> {
    return this.http.get<DrcfDocument[]>(
      `${this.apiUrl}/${submissionId}/drcf/envelopes`
    );
  }

  getDrcfRecipientView(envelopeId: string): Observable<{ url: string }> {
  const params = new HttpParams().set("envelopeId", envelopeId);

  return this.http
    .get<any>(`${environment.apiUrl}/Docusign/GetRecipientView`, { params })
    .pipe(
      map((response) => {
        const signingUrl =
          response.returnURL || response.returnUrl || response.url;

        if (!signingUrl) {
          console.error("No signing URL in response:", response);
          throw new Error("Invalid response from GetRecipientView");
        }

        return { url: signingUrl };
      })
    );
}

  updateDrcfStatus(
    submissionId: number,
    drcfStatus: DrcfStatus
  ): Observable<SaveStepResponse> {
    return this.http.put<SaveStepResponse>(
      `${this.apiUrl}/${submissionId}/drcf-status`,
      drcfStatus
    );
  }

  getDrcfDocuments(): Observable<DrcfDocument[]> {
    return this.http.get<DrcfDocument[]>(
      `${environment.apiUrl}/mips/drcf-documents`
    );
  }

  voidDrcfEnvelope(
    submissionId: number,
    envelopeId: string,
    voidReason: string
  ): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      `${this.apiUrl}/${submissionId}/drcf/void`,
      { envelopeId, voidReason }
    );
  }

  downloadDrcfSignedDocument(
    submissionId: number,
    envelopeId: string
  ): Observable<Blob> {
    const params = new HttpParams().set("envelopeId", envelopeId);

    return this.http.get(`${this.apiUrl}/${submissionId}/drcf/download`, {
      params,
      responseType: "blob",
    });
  }

  downloadDrcfTemplate(entityType: string): Observable<Blob> {
    const params = new HttpParams().set("entityType", entityType);

    return this.http.get(`${this.apiUrl}/drcf/template`, {
      params,
      responseType: "blob",
    });
  }

  // ============================================
  // FILE REPOSITORY
  // ============================================

  getFileRepository(): Observable<MipsFileRepositoryItem[]> {
    return this.http.get<MipsFileRepositoryItem[]>(this.fileRepositoryUrl);
  }

  downloadRepositoryDocument(
    submissionId: number,
    documentId: number
  ): Observable<Blob> {
    const params = new HttpParams().set("asAttachment", "true");

    return this.http.get(
      `${this.fileRepositoryUrl}/${submissionId}/${documentId}/download`,
      {
        params,
        responseType: "blob",
      }
    );
  }

  viewRepositoryDocument(
    submissionId: number,
    documentId: number
  ): Observable<Blob> {
    const params = new HttpParams().set("asAttachment", "false");

    return this.http.get(
      `${this.fileRepositoryUrl}/${submissionId}/${documentId}/download`,
      {
        params,
        responseType: "blob",
      }
    );
  }

  deleteSubmission(submissionId: number, organizationId: number): Observable<void> {
    const params = new HttpParams().set("organizationId", organizationId.toString());
    return this.http.delete<void>(`${this.apiUrl}/${submissionId}`, { params });
  }

  getOrgSubmissions(orgId: string, performanceYear: number, tin: string): Observable<any[]> {
    const params = new HttpParams()
      .set("organizationId", orgId)
      .set("performanceYear", performanceYear.toString())
      .set("tin", tin);
    return this.http.get<any[]>(`${this.apiUrl}/by-organization`, { params });
  }
}
